// /business/dashboard — Business owner dashboard
// Shows owned places, verification status, and Stripe subscription management

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  BadgeCheck,
  Clock,
  AlertCircle,
  Plus,
  ExternalLink,
  ChevronRight,
  Building2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || '';

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Verification status badge
// ---------------------------------------------------------------------------
function SubscriptionBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
        <BadgeCheck className="w-3 h-3" /> Verified
      </span>
    );
  }
  if (status === 'past_due') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
        <AlertCircle className="w-3 h-3" /> Payment overdue
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
        Cancelled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
      Not verified
    </span>
  );
}

// ---------------------------------------------------------------------------
// Single owned place card
// ---------------------------------------------------------------------------
function OwnedPlaceCard({ place }: { place: any }) {
  const photos = (() => {
    try { return JSON.parse(place.photos_json || '[]'); } catch { return []; }
  })();
  const photo = photos[0];

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/business/${place.id}/subscribe`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json() as any).error || 'Failed');
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const manageMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/business/${place.id}/manage`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json() as any).error || 'Failed');
      return res.json() as Promise<{ url: string }>;
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Place header */}
      <div className="flex items-start gap-3 p-4">
        {photo ? (
          <img
            src={photo}
            alt={place.name}
            className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-gray-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{place.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {place.city}{place.country ? `, ${place.country}` : ''} · {place.category}
              </p>
            </div>
            <SubscriptionBadge status={place.subscription_status || 'none'} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <Link
          to={`/places/${place.id}`}
          className="inline-flex items-center gap-1 text-xs text-[#0047AB] dark:text-blue-400 hover:underline"
        >
          View listing <ExternalLink className="w-3 h-3" />
        </Link>

        <div className="flex-1" />

        {place.subscription_status === 'active' ? (
          <button
            onClick={() => manageMutation.mutate()}
            disabled={manageMutation.isPending}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {manageMutation.isPending ? 'Loading…' : 'Manage subscription'}
          </button>
        ) : (
          <button
            onClick={() => subscribeMutation.mutate()}
            disabled={subscribeMutation.isPending}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#C8102E] text-white font-medium hover:bg-[#A00D25] transition-colors disabled:opacity-50"
          >
            {subscribeMutation.isPending ? 'Loading…' : 'Get Verified — $29/mo'}
          </button>
        )}
      </div>

      {(subscribeMutation.isError || manageMutation.isError) && (
        <p className="px-4 pb-3 text-xs text-red-600 dark:text-red-400">
          {(subscribeMutation.error || manageMutation.error as any)?.message}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pending claims section
// ---------------------------------------------------------------------------
function PendingClaims({ claims }: { claims: any[] }) {
  if (claims.length === 0) return null;
  return (
    <div className="mt-6">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-500" />
        Pending claims
      </h2>
      <div className="space-y-2">
        {claims.map((claim) => (
          <div
            key={claim.id}
            className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800"
          >
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{claim.place_name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {claim.city} · Submitted {new Date(claim.created_at).toLocaleDateString()}
              </p>
            </div>
            <span className="text-xs text-amber-700 dark:text-amber-400">Under review</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function BusinessDashboard() {
  const { token } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'owned' | 'claims'>('owned');

  const { data: ownedData, isLoading: ownedLoading } = useQuery({
    queryKey: ['business-owned'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/business/owned`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load');
      return res.json() as Promise<{ places: any[] }>;
    },
    enabled: !!token,
  });

  const { data: claimsData, isLoading: claimsLoading } = useQuery({
    queryKey: ['business-claims'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/business/claims`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load');
      return res.json() as Promise<{ claims: any[] }>;
    },
    enabled: !!token,
  });

  if (!token) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Business Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Sign in to manage your business listings.</p>
        <Link to="/auth/login" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8102E] text-white text-sm font-medium hover:bg-[#A00D25]">
          Sign in
        </Link>
      </div>
    );
  }

  const ownedPlaces = ownedData?.places ?? [];
  const allClaims = claimsData?.claims ?? [];
  const pendingClaims = allClaims.filter((c: any) => c.status === 'pending');

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Business Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your Korean business listings and verification.
        </p>
      </div>

      {/* What verified gets you */}
      {ownedPlaces.some((p: any) => p.subscription_status !== 'active') && (
        <div className="mb-6 p-4 bg-gradient-to-r from-[#C8102E]/5 to-[#0047AB]/5 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <BadgeCheck className="w-4 h-4 text-[#C8102E]" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Verified Badge — $29/mo</span>
          </div>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <li>✓ Verified badge on your listing — builds trust with Korean expats</li>
            <li>✓ Priority placement in search results</li>
            <li>✓ Edit your business info, hours, and photos</li>
            <li>✓ Respond to reviews</li>
          </ul>
        </div>
      )}

      {/* Owned places */}
      {ownedLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : ownedPlaces.length > 0 ? (
        <div className="space-y-3">
          {ownedPlaces.map((place: any) => (
            <OwnedPlaceCard key={place.id} place={place} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            No businesses yet. Find your listing and claim it.
          </p>
          <Link
            to="/guides"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8102E] text-white text-sm font-medium hover:bg-[#A00D25]"
          >
            <Plus className="w-4 h-4" />
            Find your business
          </Link>
        </div>
      )}

      {/* Pending claims */}
      {!claimsLoading && <PendingClaims claims={pendingClaims} />}
    </div>
  );
}
